import React from "react"

import { BarChart3, Database, Map, Settings } from "lucide-react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"

import ConfigContent from "@/components/collections/ConfigContent"
import DataContent from "@/components/collections/DataContent"
import MonitoringContent from "@/components/collections/MonitoringContent"
import PlanningContent from "@/components/collections/PlanningContent"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"

import CollectionsLayout from "../layouts/CollectionsLayout"

type CollectionsTab = "monitoring" | "planning" | "config" | "data"

const Collections: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = useAuth()

  const getCurrentTab = (): CollectionsTab => {
    const path = location.pathname
    if (path.includes("/planning")) return "planning"
    if (path.includes("/config")) return "config"
    if (path.includes("/data")) return "data"
    return "monitoring"
  }

  const activeTab = getCurrentTab()

  // Config and Performance are HIVED's; a partner link to either lands on Monitoring.
  if (!can.allBoards && (activeTab === "config" || activeTab === "data")) {
    return <Navigate to="/collections/monitoring" replace />
  }

  const handleTabChange = (value: string) => {
    const tab = value as CollectionsTab
    navigate(`/collections/${tab}`, { replace: true })
  }

  return (
    <CollectionsLayout>
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Scrolls sideways on a phone. */}
        <div className="bg-white border-b border-palette-jungleGreen200 px-4 lg:px-6 py-2 flex-shrink-0 overflow-x-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="bg-palette-jungleGreen100 h-9 w-max">
              <TabsTrigger
                value="monitoring"
                className="data-[state=active]:bg-white data-[state=active]:text-palette-jungleGreen900 data-[state=active]:shadow-sm text-palette-jungleGreen600 gap-2 px-4 text-sm"
              >
                <Map size={14} />
                Monitoring
              </TabsTrigger>
              <TabsTrigger
                value="planning"
                className="data-[state=active]:bg-white data-[state=active]:text-palette-jungleGreen900 data-[state=active]:shadow-sm text-palette-jungleGreen600 gap-2 px-4 text-sm"
              >
                <BarChart3 size={14} />
                Planning
              </TabsTrigger>
              {can.allBoards && (
                <>
                  <TabsTrigger
                    value="config"
                    className="data-[state=active]:bg-white data-[state=active]:text-palette-jungleGreen900 data-[state=active]:shadow-sm text-palette-jungleGreen600 gap-2 px-4 text-sm"
                  >
                    <Settings size={14} />
                    Config
                  </TabsTrigger>
                  <TabsTrigger
                    value="data"
                    className="data-[state=active]:bg-white data-[state=active]:text-palette-jungleGreen900 data-[state=active]:shadow-sm text-palette-jungleGreen600 gap-2 px-4 text-sm"
                  >
                    <Database size={14} />
                    Performance (alpha)
                  </TabsTrigger>
                </>
              )}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          {activeTab === "monitoring" && <MonitoringContent />}
          {activeTab === "planning" && <PlanningContent />}
          {activeTab === "config" && <ConfigContent />}
          {activeTab === "data" && <DataContent />}
        </div>
      </div>
    </CollectionsLayout>
  )
}

export default Collections
